import React, { useEffect, useMemo, useState } from 'react';
import TimeSeriesChart from '../components/TimeSeriesChart';
import { fetchActivityStats } from '../services/api';
import Spinner from '../components/Spinner';
import Header from '../components/Header';
import FilterDropdown from '../components/FilterDropdown';
import { createSafeDateFromString } from '../utils/dateUtils';

const VIEW_MODES = {
  MONTH: 'month',
  YEAR: 'year',
};

// Fields aggregated for each engagement series, keyed by the chart series id.
const ENGAGEMENT_SERIES = [
  { id: '# of Monthly Engaged Users', field: 'user_count' },
  { id: '# of Monthly Commenters', field: 'comment_count' },
  { id: '# of Monthly Writers', field: 'piece_count' },
  { id: '# of Monthly Likers', field: 'like_count' },
  { id: '# of Monthly Peer Reviewers', field: 'review_count' },
  { id: '# of Monthly Readers', field: 'read_count' },
  { id: '# of Active Users', field: 'login_count' },
];

// Extract the year portion (YYYY) from a "YYYY-MM" month string.
const getYearFromMonth = (month) => month.slice(0, 4);

// Aggregate raw monthly rows into yearly totals, summing each engagement field.
const aggregateByYear = (rows) => {
  const byYear = new Map();
  rows.forEach((row) => {
    const year = getYearFromMonth(row.month);
    if (!byYear.has(year)) {
      byYear.set(year, {
        year,
        user_count: 0,
        comment_count: 0,
        piece_count: 0,
        like_count: 0,
        review_count: 0,
        read_count: 0,
        login_count: 0,
      });
    }
    const bucket = byYear.get(year);
    ENGAGEMENT_SERIES.forEach(({ field }) => {
      bucket[field] += row[field] || 0;
    });
  });
  return Array.from(byYear.values()).sort((a, b) => a.year.localeCompare(b.year));
};

// Some metrics (e.g. read_count, login_count) were not collected for the full
// historical range, so the backend reports them as 0 for those earlier periods.
// Treat leading zero buckets as missing data (null) so Nivo renders a gap
// instead of a misleading flat line anchored at zero.
const nullifyLeadingZeros = (points) => {
  const firstNonZeroIndex = points.findIndex((point) => point.y > 0);
  if (firstNonZeroIndex === -1) {
    return points.map((point) => ({ ...point, y: null }));
  }
  return points.map((point, idx) =>
    idx < firstNonZeroIndex ? { ...point, y: null } : point,
  );
};

const EngagementByType = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [viewMode, setViewMode] = useState(VIEW_MODES.MONTH);
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [selectedYears, setSelectedYears] = useState(new Set());

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchActivityStats();
        setRawData(data);
        setSelectedMonths(new Set(data.map((item) => item.month)));
        setSelectedYears(new Set(data.map((item) => getYearFromMonth(item.month))));
        setLoading(false);
      } catch (err) {
        console.error('Error loading activity stats:', err);
        setError(err.message || 'Failed to load activity statistics');
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const availableMonths = useMemo(() => {
    const months = {};
    rawData.forEach((item) => {
      months[item.month] = true;
    });
    return months;
  }, [rawData]);

  const availableYears = useMemo(() => {
    const years = {};
    rawData.forEach((item) => {
      years[getYearFromMonth(item.month)] = true;
    });
    return years;
  }, [rawData]);

  const chartData = useMemo(() => {
    if (rawData.length === 0) return [];

    if (viewMode === VIEW_MODES.YEAR) {
      if (selectedYears.size === 0) return [];
      const aggregated = aggregateByYear(rawData).filter((row) => selectedYears.has(row.year));
      if (aggregated.length === 0) return [];
      return ENGAGEMENT_SERIES.map(({ id, field }) => ({
        id,
        data: nullifyLeadingZeros(
          aggregated.map((row) => ({
            // Anchor each yearly point to January 1st so the time scale renders correctly.
            x: createSafeDateFromString(`${row.year}-01-01`),
            y: row[field] || 0,
          })),
        ),
      }));
    }

    if (selectedMonths.size === 0) return [];
    const filtered = rawData.filter((item) => selectedMonths.has(item.month));
    if (filtered.length === 0) return [];
    return ENGAGEMENT_SERIES.map(({ id, field }) => ({
      id,
      data: nullifyLeadingZeros(
        filtered.map((item) => ({
          x: createSafeDateFromString(`${item.month}-01`),
          y: item[field] || 0,
        })),
      ),
    }));
  }, [rawData, viewMode, selectedMonths, selectedYears]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-600 text-center">
          <p className="text-xl font-semibold mb-2">Error Loading Data</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const isYearView = viewMode === VIEW_MODES.YEAR;

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header pageTitle="WtW Monthly Trends: Engagement by Type " />

      {/* Filter and Definition Row */}
      <div className="px-6 pt-4 flex items-center gap-20">
        <div className="flex items-center gap-4">
          <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />

          {isYearView ? (
            <FilterDropdown
              label="Filter by Year"
              items={availableYears}
              selectedItems={selectedYears}
              onSelectionChange={setSelectedYears}
              showCount={false}
              sortItems={(itemKeys) => itemKeys.sort().reverse()}
            />
          ) : (
            <FilterDropdown
              label="Filter by Month"
              items={availableMonths}
              selectedItems={selectedMonths}
              onSelectionChange={setSelectedMonths}
              showCount={false}
              sortItems={(itemKeys) => itemKeys.sort().reverse()}
            />
          )}
        </div>

        <div className="text-gray-700 flex-1">
          <span className="font-bold">Definition of an "Engaged" user:</span> Any user who has done one or more of the following actions in a given month: liked a piece, commented on a piece, written a piece, or written a review.
        </div>
      </div>

      <div className="px-6 pb-6">
        {chartData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm">
            <TimeSeriesChart
              data={chartData}
              title=""
              xAxisLabel={isYearView ? 'Year' : 'Month'}
              yAxisLabel="Count"
              height={600}
              showLegend={true}
              legendPosition="top-right"
              tickRotation={-45}
              margin={{ top: 50, right: 180, bottom: 100, left: 85 }}
              xAxisLegendOffset={70}
              yAxisLegendOffset={-55}
              dateType={isYearView ? 'yearly' : 'monthly'}
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <p className="text-gray-500">
              {isYearView
                ? 'No data available for selected years'
                : 'No data available for selected months'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Segmented control used to switch between monthly and yearly aggregation.
const ViewModeToggle = ({ viewMode, onChange }) => {
  const baseBtn =
    'px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500';
  const activeBtn = 'bg-blue-600 text-white';
  const inactiveBtn = 'bg-white text-gray-700 hover:bg-gray-50';

  return (
    <div
      role="group"
      aria-label="Select time aggregation"
      className="inline-flex border border-gray-300 rounded-lg shadow-sm overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onChange(VIEW_MODES.MONTH)}
        aria-pressed={viewMode === VIEW_MODES.MONTH}
        className={`${baseBtn} ${
          viewMode === VIEW_MODES.MONTH ? activeBtn : inactiveBtn
        } border-r border-gray-300`}
      >
        Month
      </button>
      <button
        type="button"
        onClick={() => onChange(VIEW_MODES.YEAR)}
        aria-pressed={viewMode === VIEW_MODES.YEAR}
        className={`${baseBtn} ${viewMode === VIEW_MODES.YEAR ? activeBtn : inactiveBtn}`}
      >
        Year
      </button>
    </div>
  );
};

export default EngagementByType;
